// İSG uzmanı portalı — Kiosk cihaz yönetimi (api-contract §5.9) erişim sondası (route
// guard / sekme görünürlüğü UX'i).
//
// Asıl yetki backend'dedir: her yönetim ucu `[ModuleAction("kiosk", …)]` taşır; grant'ı
// olmayan principal → 403. Bu sonda yalnız UX görünürlüğüdür (Yönetim paneli altındaki
// "Kiosk Cihazları" sekmesi yalnız kiosk yetkisi varsa gösterilir). Sonda ucu **liste**
// (`GET /auth/kiosk`, `kiosk/list` yetkili) — `pageSize:1` ile hafif yoklanır; liste
// activationCode/secret **taşımaz** (§5.9.5) → PII/gizli sızıntı riski yok.
//   - 2xx  → erişim var (granted) → sekme görünür
//   - 403  → yetki yok (forbidden) → sekme gizli
//   - diğer (503/ağ) → servis kullanılamıyor (unavailable) → yeniden dene
//
// Admin/Ayarlar/Ziyaretçiler sondalarıyla AYNI desendir (useAdminAccess / useVisitsAccess).

import { ApiError, kioskApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { kioskKeys } from './kioskKeys';

export type KioskAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface KioskAccess {
  status: KioskAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Kiosk cihaz yönetimi erişimini backend sondasıyla belirler. Oturum açık (RequireAuth
 * altı) varsayılır. 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 503/ağ (servis)
 * ayrımı korunur.
 */
export function useKioskAccess(): KioskAccess {
  const query = useQuery({
    queryKey: kioskKeys.access(),
    queryFn: () => kioskApi.listDevices({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: KioskAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
